import React from 'react'

import { Button } from '@octane/spark'
import FocusTopFormError from 'components/FocusTopFormError'
import FormContent from 'components/FormContent'
import FormSection from 'components/FormSection'
import FormValidationMessage from 'components/FormValidationMessage/FormValidationMessage'
import InformationSection from 'components/InformationSection'
import ConsumerShoppedCollateralNotice from 'components/notices/ConsumerShoppedCollateralNotice'
import {
    FieldGroup,
    FieldInstructionBundle,
    FieldKit,
    Form,
    FormBuilder,
    FormBuilderProps,
    FormValues,
} from 'containers/FormBuilder'
import { FormikValues } from 'formik'
import AssetType from 'state/enums/AssetType'

import CASPartnerCopyWarning from '../../components/CASPartnerCopyWarning'
import config from '../../config'
import {
    StyledFinanceSection,
    StyledApplicationSection,
    StyledFormSection,
    FinanceRuleLine,
    ApplicantRuleLine,
    StyledConsentSection,
} from './ApplicationFormBuilder.styled'
import FormSaveButton from './partials/FormSaveButton'
import FormSubmissionButton from './partials/FormSubmissionButton'
import { generateFieldInstructionsWithPrefill } from './utils'
import { WisconsinInstructionFields } from './wisconsinInstructions'
import {
    WisconsinApplicantSection,
    WisconsinCoapplicantSection,
} from './WisconsinMaritalPropertySection'

export const citizenshipStatusDisclaimer = `We only provide financing to US Citizens
and Permanent Residents. We cannot accept Driver's Licenses with
the words 'Limited Term' or 'Temp Visitor'. Contracts will not
be honored for non-permanent residents.`

const strongerApplicantBannerText = `Please be sure to put the stronger
applicant as the primary applicant.`

type InfoSectionProps = {
    id: string
    bannerText: string
    height?: string
}
function CustomInformationSection({
    id,
    bannerText,
    height = undefined,
}: InfoSectionProps) {
    return (
        <InformationSection id={id} bannerText={bannerText} height={height} />
    )
}

export type ApplicationSubmissionFormInstructions = {
    financeManagerFields?: FieldInstructionBundle
    collateralFields: FieldInstructionBundle
    applicantNameFields: FieldInstructionBundle
    applicantIdentityFields: FieldInstructionBundle
    applicantPersonalFields: FieldInstructionBundle
    emailFields: FieldInstructionBundle
    contactInfoFields: FieldInstructionBundle
    residenceFields: FieldInstructionBundle
    employmentFields: FieldInstructionBundle
    coapplicantNameFields: FieldInstructionBundle
    coapplicantIdentityFields: FieldInstructionBundle
    coapplicantEmailFields: FieldInstructionBundle
    coapplicantPersonalFields: FieldInstructionBundle
    coapplicantAddressFields: FieldInstructionBundle
    coapplicantContactInfoFields: FieldInstructionBundle
    coapplicantResidenceFields: FieldInstructionBundle
    coapplicantEmploymentFields: FieldInstructionBundle
    applicationTypeFields: FieldInstructionBundle
    relationOfCoapplicantFields: FieldInstructionBundle
    consentFields: FieldInstructionBundle
    readOnlyConsentFields?: FieldInstructionBundle
    verificationFields: FieldInstructionBundle
    readOnlyVerificationFields?: FieldInstructionBundle
    tradeInFields?: FieldInstructionBundle
} & WisconsinInstructionFields

type FormValuesToBoolFunc = (formValues: FormValues) => boolean

export type ApplicationFormBuilderProps = {
    instructions: ApplicationSubmissionFormInstructions
    coapplicantTag: string
    collateralType?: string
    isApplicationJointFunc: FormValuesToBoolFunc
    showCitizenshipStatusDisclaimer: FormValuesToBoolFunc
    showCoapplicantCitizenshipStatusDisclaimer: FormValuesToBoolFunc
    showConsumerShoppedCollateralNotice?: boolean
    swapApplicantsFunc: (fieldKit: FieldKit) => void
    isCoapplicantAddressSameFunc: FormValuesToBoolFunc
    overrideDefaultInitialValues?: FormBuilderProps['overrideDefaultInitialValues']
    enableReinitialize?: FormBuilderProps['enableReinitialize']
    onSubmit: FormBuilderProps['onSubmit']
    sectionWidth?: string
    sectionMargin?: string
    isCopy?: boolean
    parentUuid?: string
    partner?: string
    setFinalize: (state: boolean) => void
    canSubmit: boolean
    disclosureLoadFailed?: boolean
    isReadOnly?: boolean
    isCrossoverApplication?: boolean
    assetType?: AssetType
}

const renderFinanceManager = (
    isCrossoverApplication: boolean | undefined,
    sectionWidth: string | undefined,
    sectionMargin: string | undefined,
    fieldKit: FieldKit<FormikValues> | undefined,
    financeManagerFields: FieldInstructionBundle | undefined,
) => {
    if (!isCrossoverApplication && financeManagerFields) {
        return (
            <StyledFinanceSection
                title=""
                width={sectionWidth}
                margin={sectionMargin}
            >
                <FormContent gridTemplate="1fr 1fr">
                    <FieldGroup
                        fieldKit={fieldKit}
                        fieldInstructionBundle={financeManagerFields}
                    />
                    <div />
                </FormContent>
                <FinanceRuleLine />
            </StyledFinanceSection>
        )
    }
}

export default function ApplicationFormBuilder(
    props: ApplicationFormBuilderProps,
): JSX.Element {
    const {
        instructions,
        coapplicantTag,
        collateralType,
        isApplicationJointFunc,
        showCitizenshipStatusDisclaimer,
        showCoapplicantCitizenshipStatusDisclaimer,
        showConsumerShoppedCollateralNotice,
        swapApplicantsFunc,
        isCoapplicantAddressSameFunc,
        overrideDefaultInitialValues,
        enableReinitialize,
        onSubmit,
        sectionWidth,
        sectionMargin,
        isCopy,
        parentUuid,
        partner,
        setFinalize,
        canSubmit,
        disclosureLoadFailed,
        isReadOnly,
        isCrossoverApplication,
        assetType,
    } = props

    let allInstructions = instructions

    if (isReadOnly) {
        allInstructions = Object.keys(allInstructions).reduce(
            (acc: ApplicationSubmissionFormInstructions, key: string) => {
                const bundleName =
                    key as keyof ApplicationSubmissionFormInstructions

                const innerBundle = allInstructions[bundleName]

                if (innerBundle) {
                    acc[bundleName] =
                        generateFieldInstructionsWithPrefill(innerBundle)
                }

                return acc
            },
            {} as ApplicationSubmissionFormInstructions,
        )
    }

    const combinedFieldsBundle: FieldInstructionBundle = Object.values(
        allInstructions,
    ).reduce<FieldInstructionBundle>((acc, instructionBundle) => {
        return {
            ...acc,
            ...instructionBundle,
        }
    }, {})

    const autoCompleteSetting = config.ENVIRONMENT === 'local' ? 'on' : 'off'
    const applicationSectionMargin = '20px 0 0 0'

    const formTitle =
        assetType === AssetType.OUTDOOR_POWER_EQUIPMENT
            ? 'Equipment'
            : 'Collateral Information'

    return (
        <FormBuilder
            fieldInstructionBundle={combinedFieldsBundle}
            onSubmit={onSubmit}
            overrideDefaultInitialValues={overrideDefaultInitialValues || {}}
            enableReinitialize={enableReinitialize}
        >
            {({ fieldKit, formKit }) => {
                return (
                    <Form autoComplete={autoCompleteSetting}>
                        <CASPartnerCopyWarning
                            isCopy={isCopy}
                            partner={partner}
                            sectionMargin={sectionMargin}
                            sectionWidth={sectionWidth}
                        />
                        {/* FINANCE MANAGER */}
                        {renderFinanceManager(
                            isCrossoverApplication,
                            sectionWidth,
                            sectionMargin,
                            fieldKit,
                            allInstructions.financeManagerFields,
                        )}
                        {/* COLLATERAL FIELDS */}
                        <StyledFormSection
                            title={formTitle}
                            subtitle={collateralType}
                            width={sectionWidth}
                            margin={sectionMargin}
                        >
                            {showConsumerShoppedCollateralNotice &&
                                parentUuid && (
                                    <FormContent gridTemplate="2fr 1fr">
                                        <ConsumerShoppedCollateralNotice
                                            applicationUuid={parentUuid}
                                        />
                                    </FormContent>
                                )}
                            <FieldGroup
                                fieldKit={fieldKit}
                                fieldInstructionBundle={
                                    allInstructions.collateralFields
                                }
                            />
                            {allInstructions.tradeInFields ? (
                                <>
                                    <ApplicantRuleLine />
                                    <h3>Additional Deal Info</h3>
                                    <FormSection
                                        title=""
                                        subtitle=""
                                        width={sectionWidth}
                                        margin={sectionMargin}
                                    >
                                        <FieldGroup
                                            fieldKit={fieldKit}
                                            fieldInstructionBundle={
                                                allInstructions.tradeInFields
                                            }
                                        />
                                    </FormSection>
                                </>
                            ) : null}
                        </StyledFormSection>

                        {/* APPLICANT FIELDS */}
                        <StyledApplicationSection
                            title="Applicant"
                            width={sectionWidth}
                            margin={applicationSectionMargin}
                        >
                            <FormContent gridTemplate="2fr 1fr">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.applicationTypeFields
                                    }
                                />
                            </FormContent>
                            <ApplicantRuleLine />
                            <h3>Personal Info</h3>

                            {isApplicationJointFunc(fieldKit.values) && (
                                <FormContent gridTemplate="2fr 1fr">
                                    {!isReadOnly && (
                                        <>
                                            <CustomInformationSection
                                                id="strongerApplicantBanner"
                                                bannerText={
                                                    strongerApplicantBannerText
                                                }
                                                height="50px"
                                            />
                                            <Button
                                                type="button"
                                                id="swapApplicantsButton"
                                                data-testid="swapApplicantsButton"
                                                size="medium"
                                                disabled={false}
                                                onClick={() => {
                                                    swapApplicantsFunc(fieldKit)
                                                }}
                                            >
                                                Swap
                                            </Button>
                                        </>
                                    )}
                                </FormContent>
                            )}

                            <FormContent gridTemplate="1fr 1fr">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.applicantNameFields
                                    }
                                />
                            </FormContent>
                            <FormContent gridTemplate="1fr 1fr 1fr">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.applicantIdentityFields
                                    }
                                />
                            </FormContent>
                            {showCitizenshipStatusDisclaimer(
                                fieldKit.values,
                            ) && (
                                <FormContent gridTemplate="1fr">
                                    <CustomInformationSection
                                        id="citizenshipStatusDisclaimer"
                                        bannerText={citizenshipStatusDisclaimer}
                                    />
                                </FormContent>
                            )}

                            <FormContent gridTemplate="repeat(12, 1fr)">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.applicantPersonalFields
                                    }
                                />
                            </FormContent>

                            <FormContent gridTemplate="repeat(6, 1fr)">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.residenceFields
                                    }
                                />
                            </FormContent>

                            <ApplicantRuleLine />
                            <h3>Contact Info</h3>
                            <FormContent gridTemplate="repeat(3, 1fr)">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.emailFields
                                    }
                                />
                            </FormContent>
                            <FormContent gridTemplate="repeat(3, 1fr)">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.contactInfoFields
                                    }
                                />
                            </FormContent>
                            <ApplicantRuleLine />

                            <h3>Income & Employment</h3>
                            <FormContent gridTemplate="repeat(24, 1fr)">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.employmentFields
                                    }
                                />
                            </FormContent>

                            <WisconsinApplicantSection
                                fieldKit={fieldKit}
                                instructions={allInstructions}
                            />

                            <ApplicantRuleLine />
                        </StyledApplicationSection>

                        {/* CO-APPLICANT FIELDS */}
                        {isApplicationJointFunc(fieldKit.values) && (
                            <StyledApplicationSection
                                title="Co-Applicant"
                                width={sectionWidth}
                                margin={sectionMargin}
                            >
                                <FormContent gridTemplate="1fr">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.relationOfCoapplicantFields
                                        }
                                    />
                                </FormContent>
                                <ApplicantRuleLine />
                                <h3>Co-Applicant Personal Info</h3>
                                <FormContent gridTemplate="1fr 1fr">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.coapplicantNameFields
                                        }
                                    />
                                </FormContent>
                                <FormContent gridTemplate="1fr 1fr 1fr">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.coapplicantIdentityFields
                                        }
                                    />
                                </FormContent>
                                {showCoapplicantCitizenshipStatusDisclaimer(
                                    fieldKit.values,
                                ) && (
                                    <FormContent gridTemplate="1fr">
                                        <CustomInformationSection
                                            id={`${coapplicantTag}CitizenshipStatusDisclaimer`}
                                            bannerText={
                                                citizenshipStatusDisclaimer
                                            }
                                        />
                                    </FormContent>
                                )}

                                <FormContent gridTemplate="repeat(24, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.coapplicantPersonalFields
                                        }
                                    />
                                </FormContent>

                                {!isCoapplicantAddressSameFunc(
                                    fieldKit.values,
                                ) && (
                                    <FormContent gridTemplate="repeat(12, 1fr)">
                                        <FieldGroup
                                            fieldKit={fieldKit}
                                            fieldInstructionBundle={
                                                allInstructions.coapplicantAddressFields
                                            }
                                        />
                                    </FormContent>
                                )}

                                <FormContent gridTemplate="repeat(6, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.coapplicantResidenceFields
                                        }
                                    />
                                </FormContent>

                                <ApplicantRuleLine />
                                <h3>Co-Applicant Contact Info</h3>
                                <FormContent gridTemplate="repeat(3, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.coapplicantEmailFields
                                        }
                                    />
                                </FormContent>
                                <FormContent gridTemplate="repeat(3, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.coapplicantContactInfoFields
                                        }
                                    />
                                </FormContent>
                                <ApplicantRuleLine />

                                <h3>Co-Applicant Employment Info</h3>
                                <FormContent gridTemplate="repeat(24, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            allInstructions.coapplicantEmploymentFields
                                        }
                                    />
                                </FormContent>

                                <WisconsinCoapplicantSection
                                    fieldKit={fieldKit}
                                    instructions={allInstructions}
                                />

                                <ApplicantRuleLine />
                            </StyledApplicationSection>
                        )}

                        <fieldset
                            disabled={disclosureLoadFailed}
                            style={
                                disclosureLoadFailed
                                    ? {
                                          opacity: 0.4,
                                          border: 'none',
                                          padding: 0,
                                          margin: 0,
                                      }
                                    : { border: 'none', padding: 0, margin: 0 }
                            }
                            data-testid="consent-sections"
                        >
                            <StyledConsentSection
                                title=""
                                subtitle=""
                                width={sectionWidth}
                                margin={sectionMargin}
                            >
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.consentFields
                                    }
                                />
                            </StyledConsentSection>
                            <StyledConsentSection
                                title=""
                                subtitle=""
                                width={sectionWidth}
                                margin={sectionMargin}
                            >
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={
                                        allInstructions.verificationFields
                                    }
                                />
                            </StyledConsentSection>
                        </fieldset>
                        {!isReadOnly && (
                            <StyledFormSection
                                title=""
                                width={sectionWidth}
                                margin={sectionMargin}
                            >
                                <FormContent gridTemplate="1fr">
                                    <FormValidationMessage
                                        message={`
                                            Please address all validation messages
                                            in the form before submitting this
                                            application
                                        `}
                                        formIsValid={formKit.isValid}
                                        submitAttempted={
                                            formKit.submitCount > 0
                                        }
                                    />
                                    <FormSubmissionButton
                                        type="submit"
                                        id="instantDecisionButton"
                                        size="large"
                                        disabled={
                                            !canSubmit || formKit.isSubmitting
                                        }
                                        data-testid="instantDecisionButton"
                                        onClick={() => setFinalize(true)}
                                    />
                                    {/* Save is disabled during submission to prevent
                                        duplicate requests while an async submit
                                        is in flight. */}
                                    <FormSaveButton
                                        type="submit"
                                        id="saveButton"
                                        size="large"
                                        disabled={formKit.isSubmitting}
                                        variant="secondary"
                                        data-testid="saveButton"
                                        onClick={() => setFinalize(false)}
                                    />
                                </FormContent>
                            </StyledFormSection>
                        )}
                        <FocusTopFormError />
                    </Form>
                )
            }}
        </FormBuilder>
    )
}

ApplicationFormBuilder.defaultProps = {
    overrideDefaultInitialValues: undefined,
    enableReinitialize: undefined,
    sectionWidth: '100%',
    sectionMargin: '0',
    showConsumerShoppedCollateralNotice: false,
    isCopy: false,
    parentUuid: undefined,
    partner: '',
    isReadOnly: false,
    isCrossoverApplication: false,
}
